

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  InputAdornment,
  IconButton,
  Box,
  Snackbar,
  Alert,
  TableContainer,
  Paper,
  Typography,
  Tooltip,
  Popper,
  CircularProgress
} from '@mui/material';
import MDButton from 'components/MDButton';
import { useAuth } from '../../auth/Auth';
import { Autocomplete } from '@mui/material';
import { v4 as uuidv4 } from "uuid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import InfoIcon from '@mui/icons-material/Info';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { frontendLogger } from '../../utils/logger';
import { useCurrency, AVAILABLE_CURRENCIES  } from 'context/CurrencyContext';

const CloneQuotationModal = ({ open,
  onClose,
  quotation,
  onClone,
  suggestions,
  customFields: customFieldsProp, // Rename prop to avoid conflict
  // customFieldsRef,
  formErrors: formErrorsProp, // Rename prop to avoid conflict
  onFieldUpdate,
  onFieldError,
  initialCustomFields }) => {
  const [status, setStatus] = useState('Draft');
  const { user, axiosInstance } = useAuth();
  const [loading, setLoading] = useState(true);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Handle both ISO string and regular date formats
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const [clonedQuotation, setClonedQuotation] = useState({
    ...quotation,
    id: undefined,
    status: 'Draft',
    deal_name: quotation?.deal_name || '',
    deal_id: quotation?.deal_id || '',
    quote_date: formatDateForInput(quotation?.quote_date) || '',
    account_name: quotation?.account_name || '',
    account_id: quotation?.account_id || '',
    custom_fields: quotation?.custom_fields || {}
  });
  const [clonedItems, setClonedItems] = useState([]);
  const [productList, setProductList] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [cloneFormData, setCloneFormData] = useState(() => {
    // Combine custom_fields and any cf_ prefixed fields from the quotation
    const customFieldData = {
      ...(quotation?.custom_fields || {}),
      ...Object.entries(quotation || {})
        .filter(([key]) => key.startsWith('cf_'))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    };
    return customFieldData;
  });
  const [accountOptions, setAccountOptions] = useState([]);
  const [dealOptions, setDealOptions] = useState([]);
  const [existingQuoteNames, setExistingQuoteNames] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const navigate = useNavigate();
  const [isCloning, setIsCloning] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  // Add state for custom fields
  const [customFields, setCustomFields] = useState(customFieldsProp || []); // Initialize with prop
  const [formErrors, setFormErrors] = useState(formErrorsProp || {});
  const customFieldsRef = useRef(null);
  const { formatCurrency } = useCurrency();

  // Add useEffect to fetch custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get('/custom-fields/quotations');
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    if (open) {
      fetchCustomFields();
    }
  }, [open, axiosInstance]);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!open) return;
      setLoading(true);

      try {
        // Fetch quotes for unique name generation
        const quotesResponse = await axiosInstance.get("/quotations/");
        const quotes = quotesResponse.data.map(quote => quote.quote_name);
        const newUniqueName = generateUniqueQuoteName(quotes);
        setExistingQuoteNames(quotes);

        // Fetch products
        const productsResponse = await axiosInstance.get("/products/");
        const products = productsResponse.data;
        setProductList(products);
        setProductOptions(products.map(product => ({
          id: product.id,
          label: product.name,
          ...product
        })));

        // Fetch accounts
        const accountsResponse = await axiosInstance.get("/accounts/");
        const accounts = accountsResponse.data.map(account => ({
          id: account.id,
          label: account.name,
          value: account.name
        }));
        setAccountOptions(accounts);

        // Fetch deals
        const dealsResponse = await axiosInstance.get("/deals/");
        const deals = dealsResponse.data.map(deal => ({
          id: deal.id,
          label: deal.name,
          value: deal.name
        }));
        setDealOptions(deals);

        let dealName = quotation.deal_name || '';
        if (quotation.deal_id) {
          try {
            const response = await axiosInstance.get(`/deals/${quotation.deal_id}`);
            dealName = response.data.name;
          } catch (error) {
            console.error("Error fetching deal details:", error);
          }
        }

        // Set cloned quotation data
        setClonedQuotation(prev => ({
          ...prev,
          quote_name: newUniqueName,
          deal_name: dealName,
          deal_id: quotation.deal_id || '',
          quote_date: formatDateForInput(quotation.quote_date) || '',
          account_name: quotation.account_name || '',
          account_id: quotation.account_id || '',
          custom_fields: quotation.custom_fields || {}
        }));

        // Fetch quotation items if quotation has an ID
        if (quotation.id) {
          const itemsResponse = await axiosInstance.get(`/quotations/items/${quotation.id}`);
          const itemsClone = itemsResponse.data.map(item => ({
            ...item,
            id: uuidv4(),
            quotation_id: undefined,
            product_name: item.product_name || '',
            price: Number(item.price) || 0,
            tax: Number(item.tax) || 0,
            quantity: Number(item.quantity) || 1,
            discount_percentage: Number(item.discount_percentage) || 0,
            discounted_price: Number(item.discounted_price) || 0,
            total_price: Number(item.total_price) || 0
          }));
          setClonedItems(itemsClone);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({
          open: true,
          message: "Error loading data. Please try again.",
          severity: "error"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [open, quotation, axiosInstance]);

  // Function to generate a unique quote name (same as AddQuotationModal)
  const generateUniqueQuoteName = (existingNames) => {
    let uniqueName = '';
    let number = 1;

    // Find the maximum existing number and start from the next
    const existingNumbers = existingNames
      .map(name => {
        const match = name.match(/Quote-(\d{4})$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(num => num > 0);

    number = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

    // Always create a unique name
    while (true) {
      uniqueName = `Quote-${String(number).padStart(4, '0')}`;
      if (!existingNames.includes(uniqueName)) {
        break;
      }
      number++;
    }

    console.log("Generated Unique Quote Name:", uniqueName);
    return uniqueName;
  };

  useEffect(() => {
    if (quotation && open) {
      setCloneFormData({
        ...quotation.custom_fields,
        // Spread any custom fields directly from quotation
        ...Object.entries(quotation)
          .filter(([key]) => key.startsWith('cf_'))
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      });
    }
  }, [quotation, open]);

  // Fetch existing quote names when modal opens
  useEffect(() => {
    const fetchExistingQuotes = async () => {
      if (open) {
        try {
          const response = await axiosInstance.get('/quotations/');
          const quotes = response.data.map(quote => quote.quote_name);
          const newUniqueName = generateUniqueQuoteName(quotes);

          setExistingQuoteNames(quotes);
          setClonedQuotation(prev => ({
            ...prev,
            quote_name: newUniqueName,
            deal_name: quotation.deal_name || '',
            deal_id: quotation.deal_id || '',
            quote_date: formatDateForInput(quotation.quote_date) || '',
            custom_fields: quotation.custom_fields || {}
          }));

          setCloneFormData(quotation.custom_fields || {});
        } catch (error) {
          console.error("Error fetching existing quotes:", error);

          // More robust fallback
          const fallbackName = `Quote-${String(Date.now()).slice(-4)}`;
          setClonedQuotation(prev => ({
            ...prev,
            quote_name: fallbackName,
            deal_name: '',
            deal_id: '',
            quote_date: '',
            custom_fields: {}
          }));
          setExistingQuoteNames([]);
        }
      }
    };

    fetchExistingQuotes();
  }, [axiosInstance, open, quotation]);

  useEffect(() => {
    if (open && quotation) {
      // Sync custom fields data when modal opens
      const customFieldData = {
        ...(quotation.custom_fields || {}),
        ...Object.entries(quotation)
          .filter(([key]) => key.startsWith('cf_'))
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      };

      setCloneFormData(customFieldData);
      setClonedQuotation(prev => ({
        ...prev,
        custom_fields: customFieldData
      }));
    }
  }, [open, quotation]);

  const handleFieldChange = (key, value) => {
    if (key === 'quote_date' && value < today) {
      // Prevent setting a past date
      return;
    }
    setClonedQuotation(prev => ({ ...prev, [key]: value }));
  };

  const handleAddItem = () => {
    setClonedItems(prev => [...prev, {
      id: uuidv4(),
      product_name: '',
      price: 0,
      tax: 0,
      quantity: 1,
      discount_percentage: 0,
      discounted_price: 0,
      total_price: 0
    }]);
  };

  const handleRemoveItem = (index) => {
    if (clonedItems.length > 1) {
      setClonedItems(prev => prev.filter((_, idx) => idx !== index));
    }
  };

  const handleProductSelect = (index, selectedOption) => {
    setClonedItems(prev => prev.map((item, idx) => {
      if (idx === index) {
        if (!selectedOption) {
          // Reset all related fields when product is deselected
          return {
            ...item,
            product_id: undefined,
            product_name: '',
            price: 0,
            tax: 0,
            quantity: 0,
            discount_percentage: 0,
            discounted_price: 0,
            total_price: 0
          };
        }

        // If a product is selected, proceed with existing logic
        const price = Number(selectedOption.price) || 0;
        const tax = Number(selectedOption.tax_rate) || 0;
        const quantity = 0; // Set initial quantity to 0
        const discount_percentage = 0; // Set initial discount to 0

        // Calculate discounted price (price - discount)
        const discounted_price = price * (1 - discount_percentage / 100);

        // Tax on discounted unit, then × quantity
        const taxAmount = (discounted_price * tax / 100);
        const total_price = (discounted_price + taxAmount) * quantity;

        return {
          ...item,
          product_id: selectedOption.id,
          product_name: selectedOption.label,
          price,
          tax,
          quantity,
          discount_percentage,
          discounted_price,
          total_price
        };
      }
      return item;
    }));

    // Update quotation totals
    updateQuotationTotals();
  };

  const handleItemChange = (index, field, value) => {
    if (field === "discount_percentage") {
      if (value > 100) {
        value = 100;
      } else if (value < 0) {
        value = 0;
      }
    }

    setClonedItems(prev => prev.map((item, idx) => {
      if (idx === index) {
        const updatedItem = { ...item, [field]: value };

        // Recalculate prices
        const price = Number(updatedItem.price) || 0;
        const tax = Number(updatedItem.tax) || 0;
        const quantity = Number(updatedItem.quantity) || 0;
        const discount_percentage = Number(updatedItem.discount_percentage) || 0;

        // Calculate discounted price (price - discount)
        const discounted_price = price * (1 - discount_percentage / 100);

        // Line net (tax applied in updateQuotationTotals / calculateSummary)
        const taxAmount = (discounted_price * tax / 100);
        const total_price = (discounted_price + taxAmount) * quantity;

        return {
          ...updatedItem,
          discounted_price,
          total_price
        };
      }
      return item;
    }));

    // Update quotation totals
    updateQuotationTotals();
  };

  const updateQuotationTotals = () => {
    let subtotal = 0;
    let taxSummary = {};

    clonedItems.forEach((item) => {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 0;
      const tax = Number(item.tax) || 0;
      const discount_percentage = Number(item.discount_percentage) || 0;

      // Calculate base price (before tax)
      const discounted_price = price * (1 - discount_percentage / 100);
      const baseAmount = discounted_price * quantity;
      subtotal += baseAmount;

      // Tax on discounted net (after discount).
      if (tax) {
        const taxAmount = baseAmount * (tax / 100);
        taxSummary[tax] = (taxSummary[tax] || 0) + taxAmount;
      }
    });

    const total = subtotal + Object.values(taxSummary).reduce((sum, tax) => sum + tax, 0);

    setClonedQuotation(prev => ({
      ...prev,
      subtotal: subtotal.toFixed(2),
      total_amount: total.toFixed(2)
    }));
  };


  const formatIndianNumber = (number) => {
    if (isNaN(number) || number === null || number === undefined) return "0.00";

    const [integerPart, decimalPart] = Number(number).toFixed(2).split(".");
    const lastThreeDigits = integerPart.slice(-3);
    const otherDigits = integerPart.slice(0, -3);

    let formattedNumber = "";
    if (otherDigits) {
      formattedNumber = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThreeDigits;
    } else {
      formattedNumber = lastThreeDigits;
    }

    return `${formattedNumber}.${decimalPart}`; // Always include two decimal places
  };

  const handleClone = async () => {
    setIsCloning(true);
    try {
      if (clonedQuotation.quote_date < today) {
        setSnackbar({
          open: true,
          message: "Cannot clone quotation with a past expiration date.",
          severity: 'error'
        });
        return;
      }

      // Calculate tax summary and totals
      const { subtotal, grandTotal, taxSummary } = calculateSummary();

      // Extract tax rates and amounts for tax slabs
      const taxSlabs = taxSummary.map(item => item.taxRate);
      const taxAmounts = taxSummary.map(item => item.amount);

      // Prepare the quotation data with custom fields
      const quotationData = {
        ...clonedQuotation,
        quote_name: generateUniqueQuoteName(existingQuoteNames),
        status: 'Draft',
        created_by: user?.id,
        updated_by: user?.id,
        created_by_name: user?.username,
        updated_by_name: user?.username,
        custom_fields: {
          ...quotation?.custom_fields,
          ...cloneFormData
        },
        // Include subtotal and total amount
        subtotal: subtotal,
        total_amount: grandTotal,
        // Add tax slabs and amounts
        tax_slab_1: taxSlabs[0] || null,
        tax_slab_2: taxSlabs[1] || null,
        tax_slab_3: taxSlabs[2] || null,
        tax_slab_4: taxSlabs[3] || null,
        tax_slab_5: taxSlabs[4] || null,
        tax_slab_amount_1: taxAmounts[0] || null,
        tax_slab_amount_2: taxAmounts[1] || null,
        tax_slab_amount_3: taxAmounts[2] || null,
        tax_slab_amount_4: taxAmounts[3] || null,
        tax_slab_amount_5: taxAmounts[4] || null,
      };

      // Create new quotation
      const quotationResponse = await axiosInstance.post('/quotations/', quotationData);
      const newQuotationId = quotationResponse.data.id;
      // --- Log quotation creation (clone) ---
      await frontendLogger.logCreate('Quotation', {
        id: newQuotationId,
        action: 'clone',
        created_by: user.id,
        created_at: quotationResponse.data.created_at || new Date().toISOString(),
      });

      // Clone items for the new quotation
      if (clonedItems.length > 0) {
        const itemsForApi = clonedItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          price: Number(item.price),
          tax: Number(item.tax),
          quantity: Number(item.quantity),
          discount_percentage: Number(item.discount_percentage),
          discounted_price: Number(item.discounted_price),
          total_price: Number(item.total_price)
        }));

        await axiosInstance.post(`/quotations/items/${newQuotationId}`, itemsForApi);
      }

      onClose();
      setSnackbar({
        open: true,
        message: 'Quotation cloned successfully!',
        severity: 'success'
      });

      // Navigate to the new quotation
      setTimeout(() => {
        navigate(`/quote/${newQuotationId}`);
      }, 1000);

    } catch (error) {
      console.error('Error cloning quotation:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to clone quotation',
        severity: 'error'
      });
    } finally {
      setIsCloning(false);
    }
  };

  const fieldsToShow = [
    { key: 'deal_name', label: 'Deal', type: 'autocomplete', options: dealOptions },
    { key: 'account_name', label: 'Account', type: 'autocomplete', options: accountOptions },
    { key: 'status', label: 'Status', type: 'select' },
    { key: 'quote_date', label: 'Quote Expiration Date', type: 'date' },
     {
        key: 'currency',
        label: 'Currency',
        type: 'autocomplete',
        options: AVAILABLE_CURRENCIES
      },
  ];

  // Add new calculation function similar to AddQuotationModal
  const calculateSummary = () => {
    let subtotal = 0;
    let taxSummary = {};

    clonedItems.forEach((item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const discountPercentage = parseFloat(item.discount_percentage) || 0;
      const taxRate = parseFloat(item.tax) || 0;
      const discountedPrice = parseFloat(item.discounted_price);
      const discountedUnit = Number.isFinite(discountedPrice)
        ? discountedPrice
        : price * (1 - discountPercentage / 100);
      const itemNet = discountedUnit * quantity;
      subtotal += itemNet;

      if (taxRate) {
        const taxAmount = itemNet * (taxRate / 100);
        taxSummary[taxRate] = (taxSummary[taxRate] || 0) + taxAmount;
      }
    });

    const grandTotal = subtotal + Object.values(taxSummary).reduce((acc, taxAmount) => acc + taxAmount, 0);

    const taxSummaryArray = Object.keys(taxSummary).map(taxRate => ({
      taxRate: parseFloat(taxRate),
      amount: taxSummary[taxRate],
    }));

    return { subtotal, taxSummary: taxSummaryArray, grandTotal };
  };

  // Add handlers for custom fields
  const handleFieldUpdate = (field, value) => {
    setCloneFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFieldError = (field, error) => {
    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setFormErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogTitle>Clone Quotation</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {fieldsToShow.map(({ key, label, type, options }) => (
                <Grid item xs={6} key={key}>
                  {type === 'autocomplete' && key === 'currency' ? (
                    <Autocomplete
                      options={AVAILABLE_CURRENCIES}
                      value={AVAILABLE_CURRENCIES.find(opt => opt.value === clonedQuotation.currency) || null}
                      onChange={(_, newValue) => handleFieldChange('currency', newValue?.value || 'INR')}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={label}
                          fullWidth
                          margin="normal"
                          sx={{ '& .MuiInputBase-root': { height: '45px' } }}
                        />
                      )}
                    />
                  ) : key === 'status' ? (
                    <TextField
                      select
                      label={label}
                      value={clonedQuotation[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        style: { height: '45px' }
                      }}
                    >
                      {['Draft', 'Sent', 'Accepted', 'Rejected'].map((status) => (
                        <MenuItem key={status} value={status}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              backgroundColor: {
                                Draft: '#FFC107',
                                Sent: '#2196F3',
                                Accepted: '#4CAF50',
                                Rejected: '#F44336'
                              }[status],
                              borderRadius: '50%',
                              mr: 1
                            }}
                          />
                          {status}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : key === 'quote_date' ? (
                    <TextField
                      label="Quote Expiration Date"
                      type="date"
                      value={clonedQuotation.quote_date || ''}
                      onChange={(e) => handleFieldChange('quote_date', e.target.value)}
                      fullWidth
                      margin="normal"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        style: { height: '45px' },
                        min: today
                      }}
                    />
                  ) : (
                    <TextField
                      label={label}
                      value={clonedQuotation[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      fullWidth
                      margin="normal"
                      disabled={key === 'subtotal' || key === 'total_amount'}
                    />
                  )}
                </Grid>
              ))}
              <Grid item xs={12}>
                <MDBox mt={2}>
                  <MDTypography variant="h6" gutterBottom>
                    Custom Fields
                  </MDTypography>
                  <CustomFieldsSection
                    ref={customFieldsRef}
                    objectType="quotations"
                    objectData={{ ...clonedQuotation, ...cloneFormData }}
                    isEditing={true}
                    onFieldUpdate={(field, value) => {
                      setCloneFormData(prev => ({ ...prev, [field]: value }));
                      setClonedQuotation(prev => ({
                        ...prev,
                        custom_fields: { ...prev.custom_fields, [field]: value }
                      }));
                      onFieldUpdate(field, value); // Propagate to parent
                    }}
                    onError={onFieldError}
                    formErrors={formErrors}
                    customFields={customFields}
                  />
                </MDBox>
              </Grid>

            </Grid>
          )}

          {/* Product Table */}
          <MDBox
            sx={{
              mt: 3,
              mb: 3,
              border: "1px solid #dee2e6",
              borderRadius: "0.75rem",
              overflow: "auto",
              maxHeight: "400px"
            }}
          >
            {/* Header */}
            <MDBox
              sx={{
                display: "flex",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #dee2e6",
                position: "sticky",
                top: 0,
                zIndex: 1,
                minWidth: '1000px'
              }}
            >
              <MDBox width="20%" p={2} textAlign="center">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Product Name
                </MDTypography>
              </MDBox>
              <MDBox width="10%" p={2} textAlign="center">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Unit Price
                </MDTypography>
              </MDBox>
              <MDBox width="10%" p={2} textAlign="center">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Tax %
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="center">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Discount %
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="center">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Discounted Price
                  <Tooltip title="Discounted Price = (Unit Price - Discount %)" arrow>
                    <IconButton size="small" sx={{ marginLeft: 0.3 }}>
                      <InfoIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="center">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Quantity
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="center">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Total Net Price
                  <Tooltip title="Total Net Price = Discounted Price * Quantity" arrow>
                    <IconButton size="small" sx={{ marginLeft: 0.3 }}>
                      <InfoIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="center">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Actions
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Body */}
            <MDBox sx={{ minWidth: '1000px' }}>
              {clonedItems.map((item, index) => (
                <MDBox
                  key={item.id}
                  display="flex"
                  sx={{
                    borderBottom: "1px solid #dee2e6",
                    '&:hover': { backgroundColor: '#f8f9fa' }
                  }}
                >
                  <MDBox width="20%" p={2} textAlign="center">
                    <Autocomplete
                      value={productOptions.find(option => option.label === item.product_name) || null}
                      onChange={(_, newValue) => handleProductSelect(index, newValue)}
                      options={productOptions}
                      getOptionLabel={(option) => option.label || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Product"
                          size="small"
                          sx={{
                            width: '90%',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#dee2e6' },
                              '&:hover fieldset': { borderColor: '#7b809a' }
                            }
                          }}
                        />
                      )}
                      PopperComponent={(props) => (
                        <Popper {...props} sx={{
                          '& .MuiAutocomplete-listbox': {
                            maxHeight: '300px',
                            width: '200px'
                          }
                        }} />
                      )}
                    />
                  </MDBox>
                  <MDBox width="10%" p={2} textAlign="center">
                    <MDTypography variant="button" fontWeight="regular">
                      {formatCurrency (item.price, quotation.currency)}
                    </MDTypography>
                  </MDBox>
                  <MDBox width="10%" p={2} textAlign="center">
                    <MDTypography variant="button" fontWeight="regular">
                      {Number(item.tax).toFixed(2)}%
                    </MDTypography>
                  </MDBox>
                  <MDBox width="12%" p={2} textAlign="center">
                    <TextField
                      type="number"
                      value={item.discount_percentage}
                      onChange={(e) => handleItemChange(index, 'discount_percentage', Math.min(Number(e.target.value), 100))}
                      InputProps={{
                        inputProps: { min: 0, max: 100 },
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                      size="small"
                      sx={{
                        width: '110%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#dee2e6' },
                          '&:hover fieldset': { borderColor: '#7b809a' }
                        }
                      }}
                    />
                  </MDBox>
                  <MDBox width="12%" p={2} textAlign="center">
                    <MDTypography variant="button" fontWeight="regular">
                      {formatCurrency(item.discounted_price, quotation.currency)}
                    </MDTypography>
                  </MDBox>
                  <MDBox width="12%" p={2} textAlign="center">
                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 1 } }}
                      size="small"
                      sx={{
                        width: '80%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#dee2e6' },
                          '&:hover fieldset': { borderColor: '#7b809a' }
                        }
                      }}
                    />
                  </MDBox>
                  <MDBox width="12%" p={2} textAlign="center">
                    <MDTypography variant="button" fontWeight="regular">
                      {formatCurrency (item.total_price, quotation.currency)}
                    </MDTypography>
                  </MDBox>
                  <MDBox width="12%" p={2} textAlign="center" display="flex" justifyContent="center" alignItems="center">
                    <IconButton onClick={() => handleRemoveItem(index)} color="error" size="small">
                      <RemoveIcon />
                    </IconButton>
                    <IconButton onClick={handleAddItem} color="info" size="small">
                      <AddIcon />
                    </IconButton>
                  </MDBox>
                </MDBox>
              ))}
            </MDBox>
          </MDBox>

          {/* Summary Section */}
          <MDBox
            sx={{
              mt: 3,
              p: 2,
              borderTop: '2px solid #dee2e6',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <MDBox sx={{ minWidth: '300px' }}>
              <MDBox display="flex" justifyContent="space-between" mb={1}>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  Subtotal:
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  {formatCurrency(calculateSummary().subtotal, clonedQuotation.currency || 'INR')}
                </MDTypography>
              </MDBox>

              {calculateSummary().taxSummary.map(({ taxRate, amount }) => (
                <MDBox key={taxRate} display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Tax ({taxRate}%):
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="medium">
                    {formatCurrency(amount, clonedQuotation.currency || 'INR')}
                  </MDTypography>
                </MDBox>
              ))}

              <MDBox
                display="flex"
                justifyContent="space-between"
                mt={2}
                pt={1.5}
                sx={{ borderTop: '1px solid #dee2e6' }}
              >
                <MDTypography variant="button" color="dark" fontWeight="medium">
                  Grand Total:
                </MDTypography>
                <MDTypography variant="button" color="info" fontWeight="bold">
                  {formatCurrency(calculateSummary().grandTotal, clonedQuotation.currency || 'INR')}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={onClose} variant="outlined" color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={() => {
              const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;
              if (!isCustomFieldsValid) {
                setSnackbar({
                  open: true,
                  message: "Please fix all errors before cloning.",
                  severity: 'error'
                });
                return;
              }
              handleClone();
            }}
            variant="gradient"
            color="info"
            disabled={isCloning}
            startIcon={isCloning ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Clone
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CloneQuotationModal;